import { useMemo } from "react";
import { ArrowLeft, ExternalLink, Smartphone, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";

function sanitizeReturnUrl(rawUrl: string): string {
  const fallback = `${window.location.origin}/dashboard`;

  if (!rawUrl) return fallback;

  try {
    const parsed = new URL(rawUrl, window.location.origin);
    const isAllowedHost =
      parsed.hostname === "flownft.org" ||
      parsed.hostname === "www.flownft.org" ||
      parsed.hostname === "flownft.lovable.app" ||
      parsed.hostname.endsWith(".lovable.app") ||
      parsed.hostname === window.location.hostname;

    return isAllowedHost ? parsed.toString() : fallback;
  } catch {
    return fallback;
  }
}

function detectWalletBrowser(): string | null {
  const ua = navigator.userAgent || "";
  if (ua.includes("Phantom")) return "Phantom";
  if (ua.includes("Solflare")) return "Solflare";
  // Phantom in-app browser on iOS often doesn't identify itself, check referrer
  if (/iPhone|iPad/.test(ua) && window.location.href.includes("phantom")) return "Phantom";
  return null;
}

export default function XShareBounce() {
  const { tweet, shareUrl, nftName, returnUrl, phantomReturnUrl, walletApp } = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const parsedTweet = params.get("tweet") ?? "";
    const parsedShareUrl = params.get("url") ?? "";
    const parsedNftName = params.get("name") ?? "NFT";
    const parsedReturnUrl = sanitizeReturnUrl(params.get("return_url") ?? `${window.location.origin}/dashboard`);
    const parsedPhantomReturnUrl = `phantom://browse/${encodeURIComponent(parsedReturnUrl)}`;
    const detectedWallet = detectWalletBrowser();

    return {
      tweet: parsedTweet,
      shareUrl: parsedShareUrl,
      nftName: parsedNftName,
      returnUrl: parsedReturnUrl,
      phantomReturnUrl: parsedPhantomReturnUrl,
      walletApp: detectedWallet,
    };
  }, []);

  const xIntentUrl = `https://x.com/intent/tweet?text=${encodeURIComponent(tweet)}&url=${encodeURIComponent(shareUrl)}`;

  const walletName = walletApp || "your wallet app";

  return (
    <main className="min-h-screen bg-background text-foreground flex items-center justify-center p-4">
      <Card className="w-full max-w-md border-border">
        <CardHeader className="text-center">
          <CardTitle>Share on X</CardTitle>
          <CardDescription>Post your tweet, then return to FLOWnft.</CardDescription>
        </CardHeader>

        <CardContent className="space-y-3">
          {/* ── Warning banner ── */}
          <Alert className="border-accent/40 bg-accent/10">
            <AlertTriangle className="h-4 w-4 text-accent" />
            <AlertDescription className="text-xs font-medium">
              After posting, <strong>switch back to {walletName}</strong> to complete the process.
              {walletApp === "Phantom" && " Tap the Phantom icon in your app switcher."}
              {walletApp === "Solflare" && " Tap the Solflare icon in your app switcher."}
              {!walletApp && " Open the wallet app you used to connect."}
            </AlertDescription>
          </Alert>

          <p className="text-sm font-medium text-center break-words">"{nftName}"</p>

          <Button className="w-full" onClick={() => window.location.assign(xIntentUrl)}>
            <ExternalLink className="w-4 h-4 mr-2" />
            Open X to Share
          </Button>

          {/* ── Primary return button (always works) ── */}
          <Button
            className="w-full animate-pulse hover:animate-none"
            onClick={() => window.location.assign(returnUrl)}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            ← Return to FLOWnft
          </Button>

          {/* ── Phantom deep link with fallback ── */}
          <Button variant="outline" className="w-full" onClick={() => {
            window.location.assign(phantomReturnUrl);
            // Fallback: if Phantom scheme doesn't open, redirect to web URL after 2s
            setTimeout(() => {
              window.location.assign(returnUrl);
            }, 2000);
          }}>
            <Smartphone className="w-4 h-4 mr-2" />
            Return via Phantom
          </Button>

          <p className="text-xs text-muted-foreground text-center break-all">{returnUrl}</p>
        </CardContent>
      </Card>
    </main>
  );
}
